import { ApiError } from './fetch';
import { apiFetchWithTimeout } from './fetch-timeout';
import { adminFetchJson, adminFetchOk } from './admin-fetch';

export type AdminDashboardDto = {
  totalUsers: number;
  totalDocuments: number;
  totalRecipients: number;
  totalDownloads: number;
  totalLeakChecks: number;
  activeSubscriptions: number;
  newUsersLast30Days: number;
  documentsLast30Days: number;
  usersByPlan: Record<string, number>;
  signupsByDay: Record<string, number>;
};

export type AdminRevenueDto = {
  mrrBrl: number;
  arrBrl: number;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  cancelledLast30Days: number;
  churnRatePercent: number;
  revenueByPlan: Record<string, number>;
  revenueTrend: { month: string; amount: number }[];
};

export type AdminActiveUsersDto = {
  dau: number;
  wau: number;
  mau: number;
};

export type AdminDashboardChartsDto = {
  days: number;
  planDistribution: Record<string, number>;
  signupsByDay: { label: string; value: number }[];
  documentsByDay: { label: string; value: number }[];
  creditsConsumedByDay: { label: string; value: number }[];
  revenueByMonth: { label: string; value: number }[];
};

export type AdminDocumentActivitySummaryDto = {
  recentFailureCount?: number;
};

export type AdminUserListItem = {
  id: string;
  email: string;
  displayName: string | null;
  plan: string;
  accountType: string;
  platformRole: string;
  emailConfirmed: boolean;
  isLockedOut: boolean;
  isMfaEnabled?: boolean;
  documentCount: number;
  createdAt: string;
  accountAccessStatus?: string;
  hasAvatar?: boolean;
  avatarUrl?: string | null;
  adminTags?: string[] | null;
  registrationClientPlatform?: string | null;
  lastAccessAt?: string | null;
  lastAccessClientPlatform?: string | null;
  lastAccessCity?: string | null;
  lastAccessCountryCode?: string | null;
};

export type AdminPagedUsersResult = {
  items: AdminUserListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type AdminTicketListItem = {
  id: string;
  subject: string;
  category: string | null;
  status: number;
  priority: number;
  userId: string;
  requesterEmail: string;
  requesterDisplayName: string | null;
  assignedToUserId: string | null;
  assigneeEmail: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  companyOwnerEmail: string | null;
  companyDisplayName: string | null;
};

export type AdminPagedTicketsResult = {
  items: AdminTicketListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type AdminDocumentInventoryStatus =
  | 'OwnerDeleted'
  | 'Archived'
  | 'Expired'
  | 'NoFile'
  | 'Active';

export type AdminDocumentStatusFilter =
  | 'active'
  | 'archived'
  | 'ownerDeleted'
  | 'expired'
  | 'noFile'
  | 'all';

export type AdminDocumentListItem = {
  id: string;
  name: string;
  description: string | null;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  recipientCount: number;
  activeTokenCount: number;
  hasLegalHold: boolean;
  processedWithVersion?: string | null;
  createdAt: string;
  fileSizeBytes: number;
  originalContentType: string;
  hasFile: boolean;
  sourcePageCount: number;
  isArchived: boolean;
  archivedAt: string | null;
  ownerDeletedAt: string | null;
  expiresAt: string | null;
  status: AdminDocumentInventoryStatus;
};

export type AdminDocumentListFilters = {
  search?: string;
  status?: AdminDocumentStatusFilter | string;
  userId?: string;
};

export type AdminPagedDocumentsResult = {
  items: AdminDocumentListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type AdminDashboardPageData = {
  dashboard: AdminDashboardDto;
  revenue: AdminRevenueDto | null;
  activeUsers: AdminActiveUsersDto | null;
  charts: AdminDashboardChartsDto | null;
  serverHealthy: boolean;
  controlSnapshot: {
    openTickets: number | null;
    openIncidents: number | null;
    openSafetyReports: number | null;
    atRiskUsers: number | null;
    pendingApprovals: number | null;
    pendingRefunds: number | null;
    pendingInvoices: number | null;
    failedDownloads1h: number | null;
    applicationErrors24h: number | null;
    staleJobs: number | null;
  };
  operations: AdminOperationsCenter | null;
};

export type AdminSessionItem = {
  id: string;
  userId: string;
  userEmail: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  clientPlatform: string | null;
  lastActivityAt: string;
  expiresAt: string;
  revokedAt: string | null;
  isSuspicious: boolean;
  isActive: boolean;
};

export type AdminPresenceUser = {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  plan: string | null;
};

export type AdminPresenceOnline = {
  onlineCount: number;
  users: AdminPresenceUser[];
};

export type AdminPresenceHistoryPoint = {
  hour: string;
  uniqueUsers: number;
};

export async function sendAdminNotification(body: {
  target: 'users' | 'broadcast' | 'segment';
  userId?: string | null;
  userIds?: string[] | null;
  segmentPlan?: string | null;
  type: string;
  title: string;
  body: string;
  inApp?: boolean;
  signalR?: boolean;
  webPush?: boolean;
  fcm?: boolean;
  sms?: boolean;
}): Promise<{ recipientCount: number }> {
  return adminFetchJson<{ recipientCount: number }>('/api/admin/notifications/send', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export type AdminCompanyMessageThread = {
  conversationId: string;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  lastMessageAt: string;
  lastPreview: string | null;
  unreadForAdmin: number;
};

export type AdminCompanyMessageItem = {
  id: string;
  conversationId: string;
  senderUserId: string;
  senderDisplayName: string;
  isFromCompany: boolean;
  caption: string | null;
  createdAt: string;
};

export async function fetchAdminCompanyThreads(): Promise<AdminCompanyMessageThread[]> {
  return adminFetchJson<AdminCompanyMessageThread[]>('/api/admin/company-messages');
}

export async function fetchAdminCompanyMessages(
  conversationId: string,
): Promise<AdminCompanyMessageItem[]> {
  return adminFetchJson<AdminCompanyMessageItem[]>(
    `/api/admin/company-messages/${encodeURIComponent(conversationId)}/messages`,
  );
}

export async function sendAdminCompanyMessage(input: {
  userId: string;
  text: string;
  conversationId?: string | null;
}): Promise<{ conversationId: string; message: AdminCompanyMessageItem }> {
  return adminFetchJson('/api/admin/company-messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function fetchAdminDashboard(): Promise<AdminDashboardDto> {
  const res = await apiFetchWithTimeout('/api/admin/dashboard');
  if (!res.ok) throw new ApiError('Failed to fetch admin dashboard', res.status);
  return (await res.json()) as AdminDashboardDto;
}

export async function fetchAdminRevenue(): Promise<AdminRevenueDto | null> {
  const res = await apiFetchWithTimeout('/api/admin/revenue');
  if (!res.ok) return null;
  return (await res.json()) as AdminRevenueDto;
}

export async function fetchAdminActiveUsers(days = 30): Promise<AdminActiveUsersDto | null> {
  const res = await apiFetchWithTimeout(`/api/admin/analytics/active-users?days=${days}`);
  if (!res.ok) return null;
  return (await res.json()) as AdminActiveUsersDto;
}

export async function fetchAdminDashboardCharts(days = 30): Promise<AdminDashboardChartsDto | null> {
  const res = await apiFetchWithTimeout(`/api/admin/dashboard/charts?days=${days}`);
  if (!res.ok) return null;
  return (await res.json()) as AdminDashboardChartsDto;
}

export async function fetchAdminDocumentActivitySummary(): Promise<AdminDocumentActivitySummaryDto> {
  const res = await apiFetchWithTimeout('/api/admin/document-activity/summary');
  if (!res.ok) throw new ApiError('Failed to fetch document activity summary', res.status);
  return (await res.json()) as AdminDocumentActivitySummaryDto;
}

export async function fetchAdminUsers(
  page: number,
  pageSize: number,
  search?: string,
  accessStatus?: string,
  adminTag?: string,
): Promise<AdminPagedUsersResult> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (search?.trim()) params.set('search', search.trim());
  if (accessStatus?.trim()) params.set('accessStatus', accessStatus.trim());
  if (adminTag?.trim()) params.set('adminTag', adminTag.trim());
  const res = await apiFetchWithTimeout(`/api/admin/users?${params}`);
  if (!res.ok) throw new ApiError('Failed to fetch admin users', res.status);
  return (await res.json()) as AdminPagedUsersResult;
}

export async function fetchAdminTickets(
  page: number,
  pageSize: number,
  status?: number,
): Promise<AdminPagedTicketsResult> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (status != null) params.set('status', String(status));
  const res = await apiFetchWithTimeout(`/api/admin/tickets?${params}`);
  if (!res.ok) throw new ApiError('Failed to fetch admin tickets', res.status);
  return (await res.json()) as AdminPagedTicketsResult;
}

export async function fetchAdminDocuments(
  page: number,
  pageSize: number,
  searchOrFilters?: string | AdminDocumentListFilters,
): Promise<AdminPagedDocumentsResult> {
  const filters: AdminDocumentListFilters =
    typeof searchOrFilters === 'string' || searchOrFilters == null
      ? { search: searchOrFilters }
      : searchOrFilters;
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (filters.search?.trim()) params.set('search', filters.search.trim());
  if (filters.status?.trim() && filters.status !== 'all') params.set('status', filters.status.trim());
  if (filters.userId?.trim()) params.set('userId', filters.userId.trim());
  const res = await apiFetchWithTimeout(`/api/admin/documents?${params}`);
  if (!res.ok) throw new ApiError('Failed to fetch admin documents', res.status);
  return (await res.json()) as AdminPagedDocumentsResult;
}

export async function fetchAdminDashboardPage(): Promise<AdminDashboardPageData> {
  const [
    dashRes,
    healthRes,
    revenueRes,
    activeUsersRes,
    opsRes,
    chartsRes,
  ] =
    await Promise.all([
      apiFetchWithTimeout('/api/admin/dashboard'),
      apiFetchWithTimeout('/ready').catch(() => null),
      apiFetchWithTimeout('/api/admin/revenue').catch(() => null),
      apiFetchWithTimeout('/api/admin/analytics/active-users?days=30').catch(() => null),
      apiFetchWithTimeout('/api/admin/operations').catch(() => null),
      apiFetchWithTimeout('/api/admin/dashboard/charts?days=30').catch(() => null),
    ]);

  if (!dashRes.ok) throw new ApiError('Failed to fetch admin dashboard', dashRes.status);

  const dashboard = (await dashRes.json()) as AdminDashboardDto;
  const revenue = revenueRes?.ok ? ((await revenueRes.json()) as AdminRevenueDto) : null;
  const activeUsers = activeUsersRes?.ok ? ((await activeUsersRes.json()) as AdminActiveUsersDto) : null;
  const charts = chartsRes?.ok ? ((await chartsRes.json()) as AdminDashboardChartsDto) : null;
  const operations = opsRes?.ok
    ? mapOperationsCenter((await opsRes.json()) as Record<string, unknown>)
    : null;
  const attention = operations?.attention;

  return {
    dashboard,
    revenue,
    activeUsers,
    charts,
    serverHealthy: healthRes?.ok ?? false,
    operations,
    controlSnapshot: {
      openTickets: attention?.openTickets ?? null,
      openIncidents: attention?.openIncidents ?? null,
      openSafetyReports: attention?.openSafetyReports ?? null,
      atRiskUsers: attention?.atRiskUsers ?? null,
      pendingApprovals: attention?.pendingAccess ?? null,
      pendingRefunds: attention?.pendingRefunds ?? null,
      pendingInvoices: attention?.pendingInvoices ?? null,
      failedDownloads1h: attention?.failedDownloads1h ?? null,
      applicationErrors24h: attention?.applicationErrors24h ?? null,
      staleJobs: attention?.staleJobs ?? null,
    },
  };
}

export type AdminSessionQuery = {
  userId?: string;
  clientPlatform?: string;
  platformFamily?: string;
  page?: number;
  pageSize?: number;
};

export async function fetchAdminSessions(
  userIdOrQuery?: string | AdminSessionQuery,
): Promise<AdminSessionItem[]> {
  const query: AdminSessionQuery =
    typeof userIdOrQuery === 'string' ? { userId: userIdOrQuery } : (userIdOrQuery ?? {});
  const result = await fetchAdminSessionsPaged(query);
  return result.items;
}

export async function fetchAdminSessionsPaged(
  query: AdminSessionQuery = {},
): Promise<AdminPagedResult<AdminSessionItem>> {
  const params = new URLSearchParams();
  if (query.userId) params.set('userId', query.userId);
  if (query.clientPlatform) params.set('clientPlatform', query.clientPlatform);
  if (query.platformFamily) params.set('platformFamily', query.platformFamily);
  if (query.page != null) params.set('page', String(query.page));
  if (query.pageSize != null) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  const res = await apiFetchWithTimeout(`/api/admin/sessions${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new ApiError('Failed to fetch admin sessions', res.status);
  const data = (await res.json()) as AdminPagedResult<AdminSessionItem> | AdminSessionItem[];
  // Backward-compat if an older API still returns a bare array.
  if (Array.isArray(data)) {
    return { items: data, totalCount: data.length, page: 1, pageSize: data.length || 200 };
  }
  return data;
}

export async function fetchAdminPresenceOnline(): Promise<AdminPresenceOnline> {
  const res = await apiFetchWithTimeout('/api/admin/presence/online');
  if (!res.ok) throw new ApiError('Failed to fetch admin presence', res.status);
  return (await res.json()) as AdminPresenceOnline;
}

export async function fetchAdminPresenceHistory(): Promise<AdminPresenceHistoryPoint[]> {
  const res = await apiFetchWithTimeout('/api/admin/presence/history');
  if (!res.ok) throw new ApiError('Failed to fetch presence history', res.status);
  return (await res.json()) as AdminPresenceHistoryPoint[];
}

// ── Paged result helper ───────────────────────────────────────

export type AdminPagedResult<T> = {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
};

// ── Incidents ─────────────────────────────────────────────────

export type AdminLeakIncident = {
  id: string;
  title: string;
  severity: string;
  status: string;
  owner: string | null;
  rootCause: string | null;
  createdAt: string;
  resolvedAt: string | null;
  slaDeadline: string | null;
};

export type AdminIncidentsDashboard = {
  incidents: AdminLeakIncident[];
  forensicEvents: {
    id: string;
    eventType: string;
    description: string;
    riskLevel: string;
    actorEmail: string | null;
    occurredAt: string;
  }[];
  keys: unknown[];
  activeIncidents: number;
  avgResolutionTime: string | null;
};

export type AdminIncidentDetail = {
  summary: AdminLeakIncident;
  uploadedFileBlobPath: string;
  reporter: { id: string; email: string; fullName: string | null };
  matchedRecipient: {
    id: string;
    name: string;
    email: string;
    fingerprintCode: string;
  } | null;
  leakedDocument: { id: string; name: string; createdAt: string } | null;
  documentOwner: { id: string; email: string; fullName: string | null } | null;
  recentDownloads: {
    id: string;
    downloadedAt: string;
    isSuccess: boolean;
    ipAddress: string | null;
    failureReason: string | null;
  }[];
  relatedEvents: { id: string; eventType: string; description: string; occurredAt: string }[];
};

export type AdminIncidentTriageRequest = {
  status: 'open' | 'investigating' | 'resolved' | 'dismissed';
  note?: string | null;
};

// ── Application errors ────────────────────────────────────────

export type AdminApplicationErrorLog = {
  id: string;
  correlationId: string;
  userId: string | null;
  userEmail: string | null;
  source: string;
  category: string;
  action: string;
  message: string;
  metadataJson: string | null;
  severity: string;
  createdAt: string;
};

// ── Document activity ─────────────────────────────────────────

export type AdminDocumentActivityEvent = {
  id: string;
  documentId: string;
  documentName: string;
  ownerUserId: string;
  ownerEmail: string | null;
  recipientId: string;
  recipientName: string;
  eventType: string;
  occurredAt: string;
  country: string | null;
  city: string | null;
  failureReason: string | null;
};

export type AdminDocumentActivityFeed = {
  items: AdminDocumentActivityEvent[];
  nextSince: string | null;
  recentFailureCount: number;
};

// ── Operations center & runbook ───────────────────────────────

export type AdminOperationsCenter = {
  services: {
    name: string;
    status: string;
    latencyMs: number;
    uptimePercent: number;
    lastCheckedAt: string;
    detail?: string | null;
  }[];
  jobs: {
    id: string;
    name: string;
    status: string;
    progressPercent: number;
    retryCount: number;
    startedAt: string;
    error: string | null;
    lastRunUtc?: string | null;
    expectedIntervalSeconds?: number;
    isStale?: boolean;
  }[];
  backups: { id: string; name: string; type: string; status: string; sizeBytes: number; createdAt: string }[];
  queues: {
    queueName: string;
    approximateDepth: number | null;
    lastHeartbeatUtc: string | null;
    status: string;
  }[];
  lastKnownBackup: { recordedAt: string; source: string } | null;
  attention: AdminAttentionQueue;
};

export type AdminAttentionQueue = {
  openTickets: number;
  openSafetyReports: number;
  openIncidents: number;
  pendingRefunds: number;
  pendingInvoices: number;
  pendingAccess: number;
  failedDownloads1h: number;
  applicationErrors24h: number;
  staleJobs: number;
  atRiskUsers: number;
  lastMercadoPagoPaymentAt: string | null;
};

export type AdminRunbookEntry = {
  id: string;
  title: string;
  description: string;
  category: string;
  severity: string;
  steps: string;
  updatedAt: string;
};

export type AdminRunbook = {
  entries: AdminRunbookEntry[];
};

// ── Global search ─────────────────────────────────────────────

export type AdminSearchHit = {
  id: string;
  type: string;
  title: string;
  subtitle: string | null;
  createdAt: string;
  parentId: string | null;
  ownerUserId: string | null;
};

export type AdminGlobalSearchResult = {
  users: AdminSearchHit[];
  documents: AdminSearchHit[];
  recipients: AdminSearchHit[];
  totalHits: number;
};

// ── User detail & audit ───────────────────────────────────────

export type AdminUserDetail = {
  id: string;
  email: string;
  displayName: string | null;
  preferredLanguage: string;
  plan: string;
  accountType: string;
  platformRole: string;
  gender: string;
  taxId?: string | null;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  stripeSubscriptionStatus?: string | null;
  emailConfirmed: boolean;
  isLockedOut: boolean;
  teamSeatCount: number;
  documentCount: number;
  recipientCount: number;
  createdAt: string;
  updatedAt: string;
  temporaryPlanExpiresAt?: string | null;
  hasGoogleLink?: boolean;
  hasAppleLink?: boolean;
  phoneNumberMasked?: string | null;
  phoneNumberConfirmed?: boolean;
  accountAccessStatus?: string;
  approvedAt?: string | null;
  adminTags?: string[] | null;
  registrationClientPlatform?: string | null;
  registrationIpAddress?: string | null;
  registrationCountry?: string | null;
  registrationRegion?: string | null;
  registrationCity?: string | null;
  registrationCountryCode?: string | null;
  lastAccessIpAddress?: string | null;
  lastAccessClientPlatform?: string | null;
  lastAccessAt?: string | null;
  lastAccessCountry?: string | null;
  lastAccessCity?: string | null;
  lastAccessCountryCode?: string | null;
};

export type AdminCreditBalance = {
  userId: string;
  userEmail: string | null;
  balance: number;
  reservedTotal: number;
  pendingShareCount: number;
  updatedAt: string;
};

export type AdminAuditLogItem = {
  id: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  ipAddress: string | null;
  createdAt: string;
};

// ── Dunning ───────────────────────────────────────────────────

export type AdminDunningUser = {
  userId: string;
  email: string;
  displayName: string | null;
  plan: string;
  subscriptionStatus: string;
  gracePeriodEndsAt: string | null;
  daysSinceFailure: number;
  monthlyValue: number;
};

export type AdminDunningDashboard = {
  failedPayments: number;
  gracePeriodUsers: number;
  churnRiskUsers: number;
  atRiskRevenue: number;
  users: AdminDunningUser[];
};

export type AdminContactSubmission = {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  category: string | null;
  createdAt: string;
  isRead: boolean;
};

export type AdminFeatureFlag = {
  name: string;
  description: string | null;
  enabled: boolean;
  rolloutPercent: number;
  targetRoles: string[];
  updatedAt: string;
};

// ── Break-glass ─────────────────────────────────────────────

export type BreakGlassAccess = {
  id: string;
  reason: string;
  requestedByEmail: string | null;
  status: string;
  expiresAt: string;
  createdAt: string;
  isActive: boolean;
};

// ── Support ticket detail ─────────────────────────────────────

export type SupportTicketMessage = {
  id: string;
  authorUserId: string;
  authorDisplayName: string | null;
  authorEmail: string | null;
  body: string;
  isStaffReply: boolean;
  createdAt: string;
};

export type SupportTicketDetail = {
  id: string;
  subject: string;
  category: string | null;
  status: number;
  priority: number;
  userId: string;
  assignedToUserId: string | null;
  createdAt: string;
  updatedAt: string;
  messages: SupportTicketMessage[];
};

export async function fetchAdminIncidents(): Promise<AdminIncidentsDashboard> {
  const raw = await adminFetchJson<Record<string, unknown>>('/api/admin/incidents');
  return {
    incidents: (raw.incidents ?? raw.Incidents ?? []) as AdminLeakIncident[],
    forensicEvents: (raw.forensicEvents ?? raw.ForensicEvents ?? []) as AdminIncidentsDashboard['forensicEvents'],
    keys: (raw.keys ?? raw.Keys ?? []) as unknown[],
    activeIncidents: Number(raw.activeIncidents ?? raw.ActiveIncidents ?? 0),
    avgResolutionTime: (raw.avgResolutionTime ?? raw.AvgResolutionTime ?? null) as string | null,
  };
}

export async function fetchAdminIncidentDetail(
  incidentId: string,
): Promise<AdminIncidentDetail> {
  return adminFetchJson<AdminIncidentDetail>(`/api/admin/incidents/${incidentId}`);
}

export async function patchAdminIncidentTriage(
  incidentId: string,
  body: AdminIncidentTriageRequest,
): Promise<AdminIncidentDetail> {
  return adminFetchJson<AdminIncidentDetail>(`/api/admin/incidents/${incidentId}/triage`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function fetchAdminApplicationErrors(
  page = 1,
  pageSize = 50,
): Promise<AdminPagedResult<AdminApplicationErrorLog>> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  return adminFetchJson<AdminPagedResult<AdminApplicationErrorLog>>(
    `/api/admin/application-errors?${params}`,
  );
}

export async function fetchAdminDocumentActivity(
  limit = 50,
  eventType?: string,
): Promise<AdminDocumentActivityFeed> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (eventType) params.set('eventType', eventType);
  return adminFetchJson<AdminDocumentActivityFeed>(
    `/api/admin/document-activity?${params}`,
  );
}

function emptyAttention(): AdminAttentionQueue {
  return {
    openTickets: 0,
    openSafetyReports: 0,
    openIncidents: 0,
    pendingRefunds: 0,
    pendingInvoices: 0,
    pendingAccess: 0,
    failedDownloads1h: 0,
    applicationErrors24h: 0,
    staleJobs: 0,
    atRiskUsers: 0,
    lastMercadoPagoPaymentAt: null,
  };
}

function mapOperationsCenter(raw: Record<string, unknown>): AdminOperationsCenter {
  const servicesRaw = (raw.services ?? raw.Services ?? []) as Record<string, unknown>[];
  const jobsRaw = (raw.jobs ?? raw.Jobs ?? []) as Record<string, unknown>[];
  const backupsRaw = (raw.backups ?? raw.Backups ?? []) as Record<string, unknown>[];
  const queuesRaw = (raw.queues ?? raw.Queues ?? []) as Record<string, unknown>[];
  const lastKnown = (raw.lastKnownBackup ?? raw.LastKnownBackup ?? null) as Record<string, unknown> | null;
  const attentionRaw = (raw.attention ?? raw.Attention ?? null) as Record<string, unknown> | null;

  const lastKnownBackup = lastKnown
    ? {
        recordedAt: String(lastKnown.recordedAt ?? lastKnown.RecordedAt ?? ''),
        source: String(lastKnown.source ?? lastKnown.Source ?? ''),
      }
    : null;

  const jobs = jobsRaw.map((j) => {
    const name = String(j.jobName ?? j.JobName ?? j.name ?? '');
    const isStale = Boolean(j.isStale ?? j.IsStale ?? false);
    const lastRun = String(j.lastRunUtc ?? j.LastRunUtc ?? j.startedAt ?? j.StartedAt ?? '');
    return {
      id: String(j.id ?? j.Id ?? name),
      name,
      status: String(j.status ?? j.Status ?? ''),
      progressPercent: Number(j.progressPercent ?? j.ProgressPercent ?? (isStale ? 0 : 100)),
      retryCount: Number(j.retryCount ?? j.RetryCount ?? 0),
      startedAt: lastRun,
      error: (j.error ?? j.Error ?? (isStale ? 'stale' : null)) as string | null,
      lastRunUtc: lastRun || null,
      expectedIntervalSeconds: Number(j.expectedIntervalSeconds ?? j.ExpectedIntervalSeconds ?? 0) || undefined,
      isStale,
    };
  });

  const backups = lastKnownBackup
    ? [{
        id: 'last-known',
        name: lastKnownBackup.source,
        type: 'database',
        status: 'completed',
        sizeBytes: 0,
        createdAt: lastKnownBackup.recordedAt,
      }]
    : backupsRaw.map((b) => ({
        id: String(b.id ?? b.Id ?? b.name ?? ''),
        name: String(b.name ?? b.Name ?? ''),
        type: String(b.type ?? b.Type ?? ''),
        status: String(b.status ?? b.Status ?? ''),
        sizeBytes: Number(b.sizeBytes ?? b.SizeBytes ?? 0),
        createdAt: String(b.createdAt ?? b.CreatedAt ?? ''),
      }));

  const attention = attentionRaw
    ? {
        openTickets: Number(attentionRaw.openTickets ?? attentionRaw.OpenTickets ?? 0),
        openSafetyReports: Number(attentionRaw.openSafetyReports ?? attentionRaw.OpenSafetyReports ?? 0),
        openIncidents: Number(attentionRaw.openIncidents ?? attentionRaw.OpenIncidents ?? 0),
        pendingRefunds: Number(attentionRaw.pendingRefunds ?? attentionRaw.PendingRefunds ?? 0),
        pendingInvoices: Number(attentionRaw.pendingInvoices ?? attentionRaw.PendingInvoices ?? 0),
        pendingAccess: Number(attentionRaw.pendingAccess ?? attentionRaw.PendingAccess ?? 0),
        failedDownloads1h: Number(attentionRaw.failedDownloads1h ?? attentionRaw.FailedDownloads1h ?? 0),
        applicationErrors24h: Number(attentionRaw.applicationErrors24h ?? attentionRaw.ApplicationErrors24h ?? 0),
        staleJobs: Number(attentionRaw.staleJobs ?? attentionRaw.StaleJobs ?? 0),
        atRiskUsers: Number(attentionRaw.atRiskUsers ?? attentionRaw.AtRiskUsers ?? 0),
        lastMercadoPagoPaymentAt: (attentionRaw.lastMercadoPagoPaymentAt ?? attentionRaw.LastMercadoPagoPaymentAt ?? null) as string | null,
      }
    : emptyAttention();

  return {
    services: servicesRaw.map((s) => ({
      name: String(s.serviceName ?? s.ServiceName ?? s.name ?? ''),
      status: String(s.status ?? s.Status ?? ''),
      latencyMs: Number(s.latencyMs ?? s.LatencyMs ?? 0),
      uptimePercent: Number(s.uptimePercent ?? s.UptimePercent ?? 0),
      lastCheckedAt: String(s.lastCheckedAt ?? s.LastCheckedAt ?? ''),
      detail: (s.detail ?? s.Detail ?? null) as string | null,
    })),
    jobs,
    backups,
    queues: queuesRaw.map((q) => ({
      queueName: String(q.queueName ?? q.QueueName ?? q.name ?? ''),
      approximateDepth: q.approximateDepth == null && q.ApproximateDepth == null
        ? null
        : Number(q.approximateDepth ?? q.ApproximateDepth),
      lastHeartbeatUtc: (q.lastHeartbeatUtc ?? q.LastHeartbeatUtc ?? null) as string | null,
      status: String(q.status ?? q.Status ?? 'unknown'),
    })),
    lastKnownBackup,
    attention,
  };
}

export async function fetchAdminOperationsCenter(): Promise<AdminOperationsCenter> {
  const raw = await adminFetchJson<Record<string, unknown>>('/api/admin/operations');
  return mapOperationsCenter(raw);
}

export async function fetchAdminRunbook(): Promise<AdminRunbook> {
  const raw = await adminFetchJson<Record<string, unknown>>('/api/admin/runbook');
  return {
    entries: (raw.entries ?? raw.Entries ?? []) as AdminRunbookEntry[],
  };
}

export async function fetchAdminGlobalSearch(q: string): Promise<AdminGlobalSearchResult> {
  const raw = await adminFetchJson<Record<string, unknown>>(`/api/admin/search?${new URLSearchParams({ q: q.trim() })}`);
  const mapHit = (h: Record<string, unknown>) => ({
    id: String(h.id ?? h.Id ?? ''),
    type: String(h.type ?? h.Type ?? ''),
    title: String(h.title ?? h.Title ?? ''),
    subtitle: (h.subtitle ?? h.Subtitle ?? null) as string | null,
    createdAt: String(h.createdAt ?? h.CreatedAt ?? ''),
    parentId: (h.parentId ?? h.ParentId ?? null) as string | null,
    ownerUserId: (h.ownerUserId ?? h.OwnerUserId ?? null) as string | null,
  });
  const users = ((raw.users ?? raw.Users ?? []) as Record<string, unknown>[]).map(mapHit);
  const documents = ((raw.documents ?? raw.Documents ?? []) as Record<string, unknown>[]).map(mapHit);
  const recipients = ((raw.recipients ?? raw.Recipients ?? []) as Record<string, unknown>[]).map(mapHit);
  return {
    users,
    documents,
    recipients,
    totalHits: Number(raw.totalHits ?? raw.TotalHits ?? 0),
  };
}

export async function fetchAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  return adminFetchJson<AdminUserDetail>(`/api/admin/users/${userId}`);
}

export async function fetchAdminUserCredits(userId: string): Promise<AdminCreditBalance> {
  return adminFetchJson<AdminCreditBalance>(`/api/admin/users/${userId}/credits`);
}

export async function addAdminUserCredits(
  userId: string,
  amount: number,
  description?: string,
): Promise<AdminCreditBalance> {
  return adminFetchJson<AdminCreditBalance>(`/api/admin/users/${userId}/credits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount, description }),
  });
}

export async function fetchAdminAuditLogs(
  page = 1,
  pageSize = 20,
  action?: string,
): Promise<AdminPagedResult<AdminAuditLogItem>> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (action) params.set('action', action);
  return adminFetchJson<AdminPagedResult<AdminAuditLogItem>>(`/api/admin/audit?${params}`);
}

export async function fetchAdminDunning(): Promise<AdminDunningDashboard> {
  return adminFetchJson<AdminDunningDashboard>('/api/admin/dunning');
}

// ── NFS-e / invoices (manual attach) ──────────────────────────

export type AdminInvoiceDto = {
  id: string;
  userId: string | null;
  itemType: string;
  itemDescription: string;
  amount: number;
  currency: string;
  status: string;
  externalInvoiceId: string | null;
  externalInvoiceUrl: string | null;
  pdfUrl: string | null;
  xmlUrl: string | null;
  customerTaxId: string | null;
  customerName: string | null;
  customerEmail: string | null;
  issuedAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  externalPaymentId: string | null;
  retryCount: number;
  lastError: string | null;
  needsManualAttachment: boolean;
};

export type AdminInvoiceSummaryDto = {
  pendingCount: number;
  missingPaymentCount: number;
  failedCount: number;
};

export type AdminMissingInvoicePaymentDto = {
  externalPaymentId: string;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  itemType: string;
  description: string;
  amount: number;
  currency: string;
  createdAt: string;
};

export async function fetchAdminInvoiceSummary(): Promise<AdminInvoiceSummaryDto> {
  return adminFetchJson<AdminInvoiceSummaryDto>('/api/admin/invoices/summary');
}

export async function fetchAdminInvoices(options: {
  page?: number;
  pageSize?: number;
  status?: string;
  needsAttachment?: boolean;
  itemType?: string;
  search?: string;
} = {}): Promise<AdminPagedResult<AdminInvoiceDto>> {
  const params = new URLSearchParams({
    page: String(options.page ?? 1),
    pageSize: String(options.pageSize ?? 25),
  });
  if (options.status) params.set('status', options.status);
  if (options.needsAttachment != null) params.set('needsAttachment', String(options.needsAttachment));
  if (options.itemType) params.set('itemType', options.itemType);
  if (options.search?.trim()) params.set('search', options.search.trim());
  return adminFetchJson<AdminPagedResult<AdminInvoiceDto>>(`/api/admin/invoices?${params}`);
}

export async function fetchAdminInvoice(id: string): Promise<AdminInvoiceDto> {
  return adminFetchJson<AdminInvoiceDto>(`/api/admin/invoices/${id}`);
}

export async function fetchAdminMissingInvoicePayments(
  page = 1,
  pageSize = 25,
): Promise<AdminPagedResult<AdminMissingInvoicePaymentDto>> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  return adminFetchJson<AdminPagedResult<AdminMissingInvoicePaymentDto>>(
    `/api/admin/invoices/missing?${params}`,
  );
}

export async function createAdminInvoiceFromPayment(body: {
  externalPaymentId: string;
  userId: string;
  itemType: string;
  itemDescription: string;
  amount: number;
  currency?: string;
}): Promise<AdminInvoiceDto> {
  return adminFetchJson<AdminInvoiceDto>('/api/admin/invoices/from-payment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function attachAdminInvoiceManual(
  id: string,
  pdf: Blob,
  pdfFileName: string,
  options?: { xml?: Blob; xmlFileName?: string; externalInvoiceId?: string },
): Promise<AdminInvoiceDto> {
  const form = new FormData();
  form.append('pdf', pdf, pdfFileName);
  if (options?.xml) form.append('xml', options.xml, options.xmlFileName ?? 'nota.xml');
  if (options?.externalInvoiceId) form.append('externalInvoiceId', options.externalInvoiceId);
  return adminFetchJson<AdminInvoiceDto>(`/api/admin/invoices/${id}/attach`, {
    method: 'POST',
    body: form,
  });
}

export async function cancelAdminInvoice(id: string): Promise<AdminInvoiceDto> {
  return adminFetchJson<AdminInvoiceDto>(`/api/admin/invoices/${id}/cancel`, { method: 'POST' });
}

export async function fetchAdminContactSubmissions(
  page = 1,
  pageSize = 20,
  isRead?: boolean,
): Promise<AdminPagedResult<AdminContactSubmission>> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (isRead != null) params.set('isRead', String(isRead));
  return adminFetchJson<AdminPagedResult<AdminContactSubmission>>(`/api/contact?${params}`);
}

export async function markAdminContactSubmissionRead(id: string): Promise<void> {
  await adminFetchOk(`/api/contact/${id}/read`, { method: 'PATCH' });
}

export async function deleteAdminContactSubmission(id: string): Promise<void> {
  await adminFetchOk(`/api/contact/${id}`, { method: 'DELETE' });
}

export async function fetchAdminFeatureFlags(): Promise<AdminFeatureFlag[]> {
  return adminFetchJson<AdminFeatureFlag[]>('/api/admin/feature-flags');
}

export async function toggleAdminFeatureFlag(flag: AdminFeatureFlag): Promise<void> {
  await adminFetchOk('/api/admin/feature-flags/toggle', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: flag.name,
      enabled: !flag.enabled,
      rolloutPercent: flag.rolloutPercent,
    }),
  });
}

export async function fetchAdminBreakGlass(): Promise<BreakGlassAccess[]> {
  const raw = await adminFetchJson<BreakGlassAccess[] | { items: BreakGlassAccess[] } | Record<string, unknown>[]>(
    '/api/admin/break-glass',
  );
  const list = Array.isArray(raw) ? raw : (raw as { items?: unknown[] }).items ?? [];
  return (list as Record<string, unknown>[]).map((item) => ({
    id: String(item.id ?? item.Id ?? ''),
    reason: String(item.reason ?? item.Reason ?? ''),
    requestedByEmail: (item.userEmail ?? item.UserEmail ?? null) as string | null,
    status: item.isActive === false || item.IsActive === false ? 'revoked' : 'active',
    expiresAt: String(item.expiresAt ?? item.ExpiresAt ?? ''),
    createdAt: String(item.createdAt ?? item.CreatedAt ?? ''),
    isActive: Boolean(item.isActive ?? item.IsActive ?? true),
  }));
}

export async function fetchAdminTicketDetail(ticketId: string): Promise<SupportTicketDetail> {
  return adminFetchJson<SupportTicketDetail>(`/api/admin/tickets/${ticketId}`);
}

// ── Mutations ─────────────────────────────────────────────────

export async function revokeAdminSession(sessionId: string): Promise<void> {
  await adminFetchOk(`/api/admin/sessions/${sessionId}/revoke`, { method: 'POST' });
}

export async function revokeAllUserSessions(userId: string): Promise<void> {
  await adminFetchOk(`/api/admin/users/${userId}/sessions/revoke-all`, { method: 'POST' });
}

export async function revokeUserTokens(userId: string): Promise<void> {
  await adminFetchOk(`/api/admin/users/${userId}/revoke-tokens`, { method: 'POST' });
}

export async function suspendAdminUser(userId: string): Promise<void> {
  await adminFetchOk(`/api/admin/users/${userId}/suspend`, { method: 'POST' });
}

export async function restoreAdminUser(userId: string): Promise<void> {
  await adminFetchOk(`/api/admin/users/${userId}/restore`, { method: 'POST' });
}

export async function forcePasswordResetAdminUser(userId: string): Promise<void> {
  await adminFetchOk(`/api/admin/users/${userId}/force-password-reset`, { method: 'POST' });
}

export async function approveAdminUser(
  userId: string,
  options?: { trialDays?: number },
): Promise<void> {
  await adminFetchOk(`/api/admin/users/${userId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ trialDays: options?.trialDays ?? 7 }),
  });
}

export async function rejectAdminUser(userId: string, reason?: string): Promise<void> {
  await adminFetchOk(`/api/admin/users/${userId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason ?? null }),
  });
}

export async function setAdminUserTag(
  userId: string,
  tag: string,
  enabled: boolean,
): Promise<AdminUserDetail> {
  return adminFetchJson<AdminUserDetail>(`/api/admin/users/${userId}/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag, enabled }),
  });
}

export async function fetchAdminApiGeo(days = 30): Promise<{
  period: number;
  totalRequests: number;
  totalWithGeo: number;
  geoCoverage: number;
  byCountry: { country: string; count: number }[];
  byCity: { country: string; region: string; city: string; count: number }[];
}> {
  return adminFetchJson(`/api/admin/analytics/geo?period=${days}`);
}

export async function fetchAdminDownloadGeo(days = 30): Promise<{
  periodDays: number;
  totalDownloads: number;
  totalWithGeo: number;
  byCountry: { country: string; count: number }[];
  byCity: { country: string; city: string | null; count: number }[];
}> {
  return adminFetchJson(`/api/admin/analytics/download-geo?days=${days}`);
}

export async function fetchAdminLandingSections(days = 30): Promise<{
  bySectionDwellSeconds: { label: string; seconds: number }[];
  bySectionViews: { label: string; count: number }[];
  byCtaClicks: { label: string; count: number }[];
}> {
  return adminFetchJson(`/api/admin/analytics/landing/sections?days=${days}`);
}

export async function fetchAdminLandingSources(days = 30): Promise<{
  byUtmSource: { label: string; count: number }[];
  byUtmMedium: { label: string; count: number }[];
  byUtmCampaign: { label: string; count: number }[];
  byReferrerDomain: { label: string; count: number }[];
}> {
  return adminFetchJson(`/api/admin/analytics/landing/sources?days=${days}`);
}

export async function fetchAdminLandingSummary(days = 30): Promise<{
  sessionsToday: number;
  sessionsYesterday: number;
  deltaPct: number | null;
  ctaClicksToday: number;
  sessionsByDay: { date: string; sessions: number }[];
  totalSessions: number;
  totalSectionViews: number;
  totalCtaClicks: number;
  avgDwellSeconds: number;
  bounceRate: number;
  convertedCount: number;
  conversionRate: number;
  topSection: string | null;
  topCta: string | null;
  avgScrollPct?: number;
  totalPageViews?: number;
}> {
  return adminFetchJson(`/api/admin/analytics/landing/summary?days=${days}`);
}

export async function fetchAdminLandingDevices(days = 30): Promise<{
  byClientPlatform: { label: string; count: number }[];
  byDevice: { label: string; count: number }[];
  byOs: { label: string; count: number }[];
  byBrowser: { label: string; count: number }[];
}> {
  return adminFetchJson(`/api/admin/analytics/landing/devices?days=${days}`);
}

export async function fetchAdminLandingPages(days = 30): Promise<{
  pages: { path: string; views: number; avgScrollPct: number; uniqueSessions: number }[];
}> {
  return adminFetchJson(`/api/admin/analytics/landing/pages?days=${days}`);
}

export async function fetchAdminLandingHeatmap(path = '/', days = 30): Promise<{
  path: string;
  days: number;
  gridW: number;
  gridH: number;
  cells: { x: number; y: number; hoverMs: number; clicks: number }[];
  totalHoverMs: number;
  totalClicks: number;
}> {
  return adminFetchJson(
    `/api/admin/analytics/landing/heatmap?path=${encodeURIComponent(path)}&days=${days}`,
  );
}

export async function fetchAdminLandingGeo(days = 30): Promise<{
  days: number;
  totalSessions: number;
  totalWithGeo: number;
  geoCoverage: number;
  byCountry: { country: string; count: number }[];
  byCity: { country: string; region: string | null; city: string; count: number }[];
}> {
  return adminFetchJson(`/api/admin/analytics/landing/geo?days=${days}`);
}

export async function patchAdminTicket(
  ticketId: string,
  body: { status?: number; priority?: number },
): Promise<SupportTicketDetail> {
  return adminFetchJson<SupportTicketDetail>(`/api/admin/tickets/${ticketId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function postAdminTicketReply(
  ticketId: string,
  body: string,
): Promise<SupportTicketDetail> {
  return adminFetchJson<SupportTicketDetail>(`/api/admin/tickets/${ticketId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
}

export async function fetchAdminSafetyReports(
  page = 1,
  pageSize = 25,
  status?: string,
  category?: string,
): Promise<AdminPagedResult<import('@tracedocuments/types').AdminSafetyReportDto>> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (status) params.set('status', status);
  if (category) params.set('category', category);
  return adminFetchJson(`/api/admin/safety-reports?${params}`);
}

export async function fetchAdminSafetyReportsSummary(): Promise<
  import('@tracedocuments/types').AdminSafetyReportsSummaryDto
> {
  return adminFetchJson('/api/admin/safety-reports/summary');
}

export async function fetchAdminSafetyReport(
  id: string,
): Promise<import('@tracedocuments/types').AdminSafetyReportDto> {
  return adminFetchJson(`/api/admin/safety-reports/${id}`);
}

export async function triageAdminSafetyReport(
  id: string,
  body: { status: string; adminNotes?: string | null },
): Promise<import('@tracedocuments/types').AdminSafetyReportDto> {
  return adminFetchJson(`/api/admin/safety-reports/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
